/**
 * Mirror of `spade-outreach-backend/src/utils/socialHandle.ts` so the
 * outreacher's LogInvite form (and the inline-edit pencil on the Invites
 * list) can show a live preview of what the backend will store. The
 * backend is still the source of truth — this only exists to give the user
 * instant feedback ("Will be saved as @khabylame") so they don't submit a
 * URL and wonder where their `@` went.
 *
 * Keep the two parsers in sync. If the backend gets new URL shapes, update
 * here too.
 */

export type SocialChannel = 'TIKTOK' | 'INSTAGRAM' | 'YOUTUBE' | 'TWITTER';

export interface SocialHandle {
  username: string;
  displayHandle: string;
  fromUrl: boolean;
}

const INSTAGRAM_URL_PATTERN =
  /^https?:\/\/(?:www\.|m\.)?instagram\.com\/(?:p\/[^/]+\/?\?[^/]*igshid=[^&]*&?[^&]*)?@?([a-zA-Z0-9._]+)(?:\/.*)?(?:\?.*)?$/i;

const TIKTOK_URL_PATTERN =
  /^https?:\/\/(?:www\.|m\.|vm\.)?tiktok\.com\/@?([a-zA-Z0-9._]+)(?:\/.*)?(?:\?.*)?$/i;

const YOUTUBE_URL_PATTERN =
  /^https?:\/\/(?:www\.|m\.)?youtube\.com\/(?:@|c\/|user\/|channel\/)?([a-zA-Z0-9._-]+)(?:\/.*)?(?:\?.*)?$/i;

const TWITTER_URL_PATTERN =
  /^https?:\/\/(?:www\.|mobile\.)?(?:x|twitter)\.com\/@?([a-zA-Z0-9_]+)(?:\/.*)?(?:\?.*)?$/i;

const USERNAME_PATTERNS: Record<SocialChannel, RegExp> = {
  TIKTOK: /^[a-zA-Z0-9._]+$/,
  INSTAGRAM: /^[a-zA-Z0-9._]+$/,
  YOUTUBE: /^[a-zA-Z0-9._-]+$/,
  TWITTER: /^[a-zA-Z0-9_]+$/,
};

const URL_PATTERNS: Record<SocialChannel, RegExp> = {
  TIKTOK: TIKTOK_URL_PATTERN,
  INSTAGRAM: INSTAGRAM_URL_PATTERN,
  YOUTUBE: YOUTUBE_URL_PATTERN,
  TWITTER: TWITTER_URL_PATTERN,
};

export function parseSocialHandle(
  channel: SocialChannel,
  raw: string,
): SocialHandle | null {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return null;

  const url = URL_PATTERNS[channel].exec(trimmed);
  if (url?.[1]) {
    return finalize(url[1], true);
  }

  const stripped = trimmed.replace(/^@+/, '');
  if (!USERNAME_PATTERNS[channel].test(stripped)) return null;
  return finalize(stripped, false);
}

function finalize(rawUsername: string, fromUrl: boolean): SocialHandle {
  const username = rawUsername.toLowerCase();
  return {
    username,
    displayHandle: `@${username}`,
    fromUrl,
  };
}
